import React, { useMemo } from "react";
import Card from "components/card";
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    flexRender,
} from "@tanstack/react-table";
import { MdDownload } from "react-icons/md";
import { Button } from '@aws-amplify/ui-react';
import { Rating } from "@aws-amplify/ui-react";
import { useDealData } from "contexts/DealContext";
import { usePortfolioData } from "contexts/PortfolioContext";
import { useViewMode } from "contexts/ViewModeContext";
import { exportCompetitionTableToCSV } from "utils/csvExport";

const columnHelper = createColumnHelper();

// Helper function to ensure number conversion - exactly like original
const ensureNumber = (val) => {
    if (val === undefined || val === null) return 0;
    if (typeof val === 'number') return val;
    if (typeof val === 'string') return parseFloat(val) || 0;
    return 0;
};

// Helper function to format cell values for competition data - using original logic
const formatCompetitionValue = (value, metric) => {
    const displayValue = value || '-';

    // Use original formatting logic
    if (metric === 'Occupancy' && displayValue !== '-') {
        return `${displayValue}%`;
    }

    return displayValue;
};

// Helper function to render star ratings - exactly like original
const renderStarRating = (rating) => {
    return (
        <Rating
            value={ensureNumber(rating)}
            readOnly={true}
            maxValue={5}
            size="small"
            fillColor="#f0b41c"
        />
    );
};

// Helper function to render percentile with progress bar - exactly like original
const renderPercentile = (percentile) => {
    if (!percentile || isNaN(Number(percentile))) return '-';
    const percent = Math.round(Number(percentile));

    return (
        <div className="w-full">
            <div className="relative pt-1">
                <div className="flex items-center justify-between">
                    <div>
                        <span className="text-xs font-semibold inline-block text-blue-600">
                            {percent}%
                        </span>
                    </div>
                </div>
                <div className="overflow-hidden h-2 text-xs flex rounded bg-blue-200">
                    <div
                        style={{ width: `${percent}%` }}
                        className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500"
                    ></div>
                </div>
            </div>
        </div>
    );
};

// Check if this is a rating metric - exactly like original
const isRatingMetric = (metric) => {
    return ['Star Rating', 'Health Inspec Rating', 'QM Rating', 'Staffing Rating'].includes(metric);
};

// Helper to determine positive/negative color for comparison stats - exactly like original
const getValueColor = (metric, competitorValue, facilityValue, isHigherBetter = true) => {
    if (metric === 'Distance') return 'text-gray-700'; // Neutral for distance
    if (!facilityValue || !competitorValue) return '';

    if (isHigherBetter) {
        return competitorValue > facilityValue ? 'text-green-600' : 'text-red-600';
    } else {
        return competitorValue < facilityValue ? 'text-green-600' : 'text-red-600';
    }
};

// Helper to determine if higher values are better for a metric
const isHigherBetter = (metric) => {
    // For most metrics, higher is better. Lower is better for: Health Survey Score, Number (rank)
    return !['Health Survey Score', 'Number'].includes(metric);
};

function TableContainer({ children, title, onExport }) {
    return (
        <Card extra={"w-full h-full p-6"}>
            <div className="mb-4 flex flex-col sm:flex-row justify-between items-start">
                <div className="flex-1 mx-2">
                    <h2 className="text-xl font-bold text-gray-700 dark:text-white">{title}</h2>
                </div>
                {onExport && (
                    <Button
                        onClick={onExport}
                        size="large"
                        className="flex items-center justify-center rounded-lg p-2 text-xl transition-opacity hover:cursor-pointer hover:opacity-80 ml-4">
                        <MdDownload />
                    </Button>
                )}
            </div>
            <div className="max-h-[90vh] overflow-auto relative border border-gray-200 rounded-lg">
                <table className="border-collapse w-full relative">
                    {children}
                </table>
            </div>
        </Card>
    );
}

function TableHeader({ headerGroups }) {
    return (
        <thead>
            {headerGroups.map((headerGroup, groupIndex) => (
                <tr key={headerGroup.id} className="sticky bg-white border-b border-gray-300" style={{ top: `${groupIndex * 40}px`, zIndex: 30 }}>
                    {headerGroup.headers.map((header, headerIndex) => {
                        // Check if this is the first column (Facility Name)
                        const isFacilityColumn = header.column.id === 'facility';
                        const isLastColumn = headerIndex === headerGroup.headers.length - 1;

                        // Enhanced styling for frozen column
                        const frozenColumnStyles = isFacilityColumn ? {
                            position: 'sticky',
                            left: 0,
                            zIndex: 40,
                            backgroundColor: 'white',
                            boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                        } : {};

                        return (
                            <th
                                key={header.id}
                                colSpan={header.colSpan}
                                onClick={header.column.getToggleSortingHandler()}
                                className={`cursor-pointer border-b border-gray-200 pb-2 pr-4 pt-2 min-w-[120px]
                                    ${isFacilityColumn ? 'text-start min-w-[200px]' : 'text-center'} 
                                    ${!isLastColumn ? 'border-r border-gray-300' : ''}
                                    dark:border-white/30`}
                                style={frozenColumnStyles}
                            >
                                <div className={`items-center justify-between text-xs 
                                    ${isFacilityColumn ? '' : 'text-center'}`}
                                >
                                    {flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                    )}
                                </div>
                            </th>
                        );
                    })}
                </tr>
            ))}
        </thead>
    );
}

// Competition Summary Table
function CompetitionSummaryTable({ facilityData, competitorData, facilityCompetitorAverage }) {
    const { currentDealName, currentFacility } = useDealData();
    const { currentPortfolioName, currentPortfolioFacility } = usePortfolioData();
    const { viewMode } = useViewMode();

    // Use view-mode-aware data
    const isPortfolioMode = viewMode === 'portfolio';
    // For export filenames, use the clean portfolio name without prefix
    const activeCurrentName = isPortfolioMode ? currentPortfolioName : currentDealName;
    const activeCurrentFacility = isPortfolioMode ? currentPortfolioFacility : currentFacility;

    // Prepare table data
    const tableData = useMemo(() => {
        const rows = [];

        if (facilityData) {
            rows.push({
                facilityName: facilityData.Facility,
                type: 'Portfolio Facility',
                starRating: facilityData['Star Rating'],
                healthInspecRating: facilityData['Health Inspec Rating'],
                qmRating: facilityData['QM Rating'],
                staffingRating: facilityData['Staffing Rating'],
                licensedBeds: facilityData['Licensed Beds'],
                occupancy: facilityData.Occupancy,
                adc: facilityData.ADC,
                healthSurveyScore: facilityData['Health Survey Score'],
                number: facilityData.Number,
                percentile: facilityData.Percentile,
                stateTotal: facilityData.State_Total,
                owner: facilityData.Owner,
                distance: null // Portfolio facility has no distance
            });
        }

        // Add consolidated competitor average row if available
        if (facilityCompetitorAverage) {
            const competitorCount = competitorData ? competitorData.length : 0;
            rows.push({
                facilityName: 'Average Competitor',
                type: 'Competitor Average',
                starRating: facilityCompetitorAverage['Star Rating'],
                healthInspecRating: facilityCompetitorAverage['Health Inspec Rating'],
                qmRating: facilityCompetitorAverage['QM Rating'],
                staffingRating: facilityCompetitorAverage['Staffing Rating'],
                licensedBeds: facilityCompetitorAverage['Licensed Beds'],
                occupancy: facilityCompetitorAverage['Occupancy'],
                adc: facilityCompetitorAverage['ADC'],
                healthSurveyScore: facilityCompetitorAverage['Health Survey Score'],
                number: facilityCompetitorAverage['Number'],
                percentile: facilityCompetitorAverage['Percentile'],
                owner: `Average of ${competitorCount} Competitors`,
                distance: facilityCompetitorAverage['Distance'] // Average distance
            });
        }

        if (competitorData && competitorData.length > 0) {
            competitorData.forEach(competitor => {
                const competitorFacilityData = competitor['facility data'];
                if (competitorFacilityData) {
                    rows.push({
                        facilityName: competitorFacilityData.Facility,
                        type: 'Competitor',
                        starRating: competitorFacilityData['Star Rating'],
                        healthInspecRating: competitorFacilityData['Health Inspec Rating'],
                        qmRating: competitorFacilityData['QM Rating'],
                        staffingRating: competitorFacilityData['Staffing Rating'],
                        licensedBeds: competitorFacilityData['Licensed Beds'],
                        occupancy: competitorFacilityData.Occupancy,
                        adc: competitorFacilityData.ADC,
                        healthSurveyScore: competitorFacilityData['Health Survey Score'],
                        number: competitorFacilityData.Number,
                        percentile: competitorFacilityData.Percentile,
                        stateTotal: competitorFacilityData.State_Total,
                        owner: competitorFacilityData.Owner,
                        distance: competitorFacilityData.Distance
                    });
                }
            });
        }

        return rows;
    }, [facilityData, competitorData, facilityCompetitorAverage]);

    const columns = useMemo(() => [
        columnHelper.accessor("facilityName", {
            id: "facility",
            header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">FACILITY</p>,
            cell: (info) => {
                const row = info.row.original;
                return (
                    <div>
                        <p className="text-sm font-bold text-gray-900">{info.getValue() || '-'}</p>
                        <p className="text-xs text-gray-500">{row.type}</p>
                        {row.distance && (
                            <p className="text-xs text-blue-600">
                                {row.type === 'Competitor Average' ? `Avg ${row.distance} miles` : `${row.distance} miles`}
                            </p>
                        )}
                        {row.type === 'Competitor Average' && !row.distance && (
                            <p className="text-xs text-gray-400">Consolidated View</p>
                        )}
                    </div>
                );
            },
        }),
        columnHelper.accessor("starRating", {
            header: () => <p className="text-sm font-bold text-center text-gray-600 dark:text-white">STAR RATING</p>,
            cell: (info) => (
                <div className="flex justify-center">
                    {renderStarRating(info.getValue())}
                </div>
            ),
        }),
        columnHelper.accessor("healthInspecRating", {
            header: () => <p className="text-sm font-bold text-center text-gray-600 dark:text-white">HEALTH INSPEC</p>,
            cell: (info) => (
                <div className="flex justify-center">
                    {renderStarRating(info.getValue())}
                </div>
            ),
        }),
        columnHelper.accessor("qmRating", {
            header: () => <p className="text-sm font-bold text-center text-gray-600 dark:text-white">QM RATING</p>,
            cell: (info) => (
                <div className="flex justify-center">
                    {renderStarRating(info.getValue())}
                </div>
            ),
        }),
        columnHelper.accessor("staffingRating", {
            header: () => <p className="text-sm font-bold text-center text-gray-600 dark:text-white">STAFFING</p>,
            cell: (info) => (
                <div className="flex justify-center">
                    {renderStarRating(info.getValue())}
                </div>
            ),
        }),
        columnHelper.accessor("licensedBeds", {
            header: () => <p className="text-sm font-bold text-center text-gray-600 dark:text-white">BEDS</p>,
            cell: (info) => {
                const row = info.row.original;
                const isCompetitor = row.type === 'Competitor' || row.type === 'Competitor Average';
                const colorClass = isCompetitor && facilityData
                    ? getValueColor('Licensed Beds', info.getValue(), facilityData['Licensed Beds'], isHigherBetter('Licensed Beds'))
                    : '';
                return (
                    <p className={`text-center text-sm font-medium ${colorClass || 'text-gray-900'}`}>
                        {formatCompetitionValue(info.getValue(), 'Licensed Beds')}
                    </p>
                );
            },
        }),
        columnHelper.accessor("occupancy", {
            header: () => <p className="text-sm font-bold text-center text-gray-600 dark:text-white">OCCUPANCY</p>,
            cell: (info) => {
                const row = info.row.original;
                const isCompetitor = row.type === 'Competitor' || row.type === 'Competitor Average';
                const colorClass = isCompetitor && facilityData
                    ? getValueColor('Occupancy', info.getValue(), facilityData.Occupancy, isHigherBetter('Occupancy'))
                    : '';
                return (
                    <p className={`text-center text-sm font-medium ${colorClass || 'text-gray-900'}`}>
                        {formatCompetitionValue(info.getValue(), 'Occupancy')}
                    </p>
                );
            },
        }),
        columnHelper.accessor("adc", {
            header: () => <p className="text-sm font-bold text-center text-gray-600 dark:text-white">ADC</p>,
            cell: (info) => {
                const row = info.row.original;
                const isCompetitor = row.type === 'Competitor' || row.type === 'Competitor Average';
                const colorClass = isCompetitor && facilityData
                    ? getValueColor('ADC', info.getValue(), facilityData.ADC, isHigherBetter('ADC'))
                    : '';
                return (
                    <p className={`text-center text-sm font-medium ${colorClass || 'text-gray-900'}`}>
                        {formatCompetitionValue(info.getValue(), 'ADC')}
                    </p>
                );
            },
        }),
        columnHelper.accessor("healthSurveyScore", {
            header: () => <p className="text-sm font-bold text-center text-gray-600 dark:text-white">HEALTH SCORE</p>,
            cell: (info) => {
                const row = info.row.original;
                const isCompetitor = row.type === 'Competitor' || row.type === 'Competitor Average';
                const colorClass = isCompetitor && facilityData
                    ? getValueColor('Health Survey Score', info.getValue(), facilityData['Health Survey Score'], isHigherBetter('Health Survey Score'))
                    : '';
                return (
                    <p className={`text-center text-sm font-medium ${colorClass || 'text-gray-900'}`}>
                        {formatCompetitionValue(info.getValue(), 'Health Survey Score')}
                    </p>
                );
            },
        }),
        columnHelper.accessor("percentile", {
            header: () => <p className="text-sm font-bold text-center text-gray-600 dark:text-white">PERCENTILE</p>,
            cell: (info) => {
                const row = info.row.original;
                const isCompetitor = row.type === 'Competitor' || row.type === 'Competitor Average';
                const colorClass = isCompetitor && facilityData
                    ? getValueColor('Percentile', info.getValue(), facilityData.Percentile, isHigherBetter('Percentile'))
                    : '';
                return (
                    <div className={`flex justify-center w-full px-2 ${colorClass}`}>
                        {renderPercentile(info.getValue())}
                    </div>
                );
            },
        }),
        columnHelper.accessor("number", {
            header: () => <p className="text-sm font-bold text-center text-gray-600 dark:text-white">RANK</p>,
            cell: (info) => {
                const row = info.row.original;
                const isCompetitor = row.type === 'Competitor' || row.type === 'Competitor Average';
                const colorClass = isCompetitor && facilityData
                    ? getValueColor('Number', info.getValue(), facilityData.Number, isHigherBetter('Number'))
                    : '';

                // Don't show rank for average competitor (since averaging ranks doesn't make sense)
                if (row.type === 'Competitor Average') {
                    return (
                        <p className={`text-center text-sm font-medium ${colorClass || 'text-gray-900'}`}>
                            -
                        </p>
                    );
                }

                // Format rank as "X out of Y" if State_Total is available
                const rank = info.getValue();
                const stateTotal = row.stateTotal;
                const displayValue = (rank && stateTotal)
                    ? `${rank} of ${stateTotal}`
                    : formatCompetitionValue(rank, 'Number');

                return (
                    <p className={`text-center text-sm font-medium ${colorClass || 'text-gray-900'}`}>
                        {displayValue}
                    </p>
                );
            },
        }),
        columnHelper.accessor("owner", {
            header: () => <p className="text-sm font-bold text-center text-gray-600 dark:text-white">OWNER</p>,
            cell: (info) => (
                <p className="text-center text-sm text-gray-900 font-medium truncate max-w-[150px]">
                    {formatCompetitionValue(info.getValue(), 'Owner')}
                </p>
            ),
        }),
    ], [facilityData]);

    const table = useReactTable({
        data: tableData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const handleExport = () => {
        const entityName = activeCurrentName || (isPortfolioMode ? 'portfolio' : 'deal');
        const facilityName = activeCurrentFacility?.details?.Facilities || 'facility';
        const filename = `${entityName}_${facilityName}_competition_analysis.csv`;

        exportCompetitionTableToCSV(
            tableData,
            columns,
            filename
        );
    };

    return (
        <TableContainer
            title="Competition Analysis"
            onExport={handleExport}
        >
            <TableHeader headerGroups={table.getHeaderGroups()} />
            <tbody>
                {table.getRowModel().rows.map((row, index) => (
                    <tr
                        key={row.id}
                        className={`
                            border-t
                            ${row.original.type === 'Portfolio Facility'
                                ? 'bg-blue-50 font-semibold border-t-2 border-blue-400'
                                : row.original.type === 'Competitor Average'
                                    ? 'bg-orange-50 font-semibold border-t-2 border-orange-400'
                                    : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                            }
                        `}
                    >
                        {row.getVisibleCells().map((cell, cellIndex) => {
                            const isFacilityColumn = cell.column.id === 'facility';
                            const isLastColumn = cellIndex === row.getVisibleCells().length - 1;

                            // Enhanced styling for frozen column
                            const frozenColumnStyles = isFacilityColumn ? {
                                position: 'sticky',
                                left: 0,
                                zIndex: 20,
                                backgroundColor: 'inherit',
                                boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                            } : {};

                            return (
                                <td
                                    key={cell.id}
                                    className={`px-4 py-3 text-sm border-gray-200 
                                        ${!isLastColumn ? 'border-r' : ''}
                                        ${isFacilityColumn ? 'min-w-[200px]' : 'min-w-[120px]'}`}
                                    style={frozenColumnStyles}
                                >
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </TableContainer>
    );
}

// Consolidated Competition Table for portfolio view
function ConsolidatedCompetitionTable({ portfolioAverages, competitorAverages, consolidatedFacility, currentDeal }) {
    const { currentDealName, currentFacility } = useDealData();
    const { currentPortfolioName, currentPortfolioFacility, uniqueFacilityCount } = usePortfolioData();
    const { viewMode } = useViewMode();

    // Use view-mode-aware data
    const isPortfolioMode = viewMode === 'portfolio';
    // For export filenames, use the clean portfolio name without prefix
    const activeCurrentName = isPortfolioMode ? currentPortfolioName : currentDealName;
    const activeCurrentFacility = isPortfolioMode ? currentPortfolioFacility : currentFacility;

    const numberOfFacilities = isPortfolioMode
        ? uniqueFacilityCount
        : currentDeal?.facilities
            ? Object.keys(currentDeal.facilities).filter(name => name.toLowerCase() !== 'consolidated').length
            : 0;

    // Get period-specific data like occupancy - exactly like original
    const periodData = activeCurrentFacility?.periods ? Object.values(activeCurrentFacility.periods)[0]?.Categories : {};
    const occupancyData = periodData?.Occupancy || [];

    // Get occupancy percentage - exactly like original
    let occupancyValue = occupancyData?.find(item => item.Type === "Occupancy")?.Percentages || consolidatedFacility?.['Occupancy'] || 0;
    if (typeof occupancyValue === 'string') {
        occupancyValue = parseFloat(occupancyValue);
    }

    // Prepare table data
    const tableData = useMemo(() => {
        const rows = [];

        // Portfolio averages row - using exact original logic
        const facilityRating = consolidatedFacility?.["Star Rating"] ?? 0;
        const healthInspection = consolidatedFacility?.["Health Inspection Star Rating"] ?? 0;
        const staffingStarRating = consolidatedFacility?.["Staffing Star Rating"] ?? 0;

        // Find quality rating among the detail keys - exactly like original
        const quality = consolidatedFacility ? Object.keys(consolidatedFacility).reduce((acc, key) => {
            if (key.toLowerCase().includes("quality")) {
                return consolidatedFacility[key] ?? 0;
            }
            return acc;
        }, 0) : 0;

        rows.push({
            facilityName: 'Portfolio Average',
            type: 'Portfolio',
            starRating: facilityRating,
            healthInspecRating: healthInspection,
            qmRating: quality,
            staffingRating: staffingStarRating,
            licensedBeds: portfolioAverages?.['Licensed Beds'],
            occupancy: occupancyValue,
            adc: portfolioAverages?.['ADC'],
            healthSurveyScore: portfolioAverages?.['Health Survey Score'],
            number: portfolioAverages?.['Number'],
            percentile: portfolioAverages?.['Percentile'],
            stateTotal: portfolioAverages?.['State_Total'],
            owner: `${consolidatedFacility?.['Total Facilities'] || numberOfFacilities || '-'} Facilities`,
            distance: null
        });

        // Competitor averages row
        if (competitorAverages) {
            rows.push({
                facilityName: 'Market Average',
                type: 'Competitor Average',
                starRating: competitorAverages['Star Rating'],
                healthInspecRating: competitorAverages['Health Inspec Rating'],
                qmRating: competitorAverages['QM Rating'],
                staffingRating: competitorAverages['Staffing Rating'],
                licensedBeds: competitorAverages['Licensed Beds'],
                occupancy: competitorAverages['Occupancy'],
                adc: competitorAverages['ADC'],
                healthSurveyScore: competitorAverages['Health Survey Score'],
                number: competitorAverages['Number'],
                percentile: competitorAverages['Percentile'],
                stateTotal: competitorAverages['State_Total'],
                owner: 'Market Competitors',
                distance: null
            });
        }

        return rows;
    }, [portfolioAverages, competitorAverages, consolidatedFacility, numberOfFacilities, occupancyValue]);

    const columns = useMemo(() => [
        columnHelper.accessor("facilityName", {
            id: "facility",
            header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">COMPARISON</p>,
            cell: (info) => {
                const row = info.row.original;
                return (
                    <div>
                        <p className="text-sm font-bold text-gray-900">{info.getValue() || '-'}</p>
                        <p className="text-xs text-gray-500">{row.type}</p>
                        <p className="text-xs text-blue-600">{row.owner}</p>
                    </div>
                );
            },
        }),
        columnHelper.accessor("starRating", {
            header: () => <p className="text-sm font-bold text-center text-gray-600 dark:text-white">STAR RATING</p>,
            cell: (info) => (
                <div className="flex justify-center">
                    {renderStarRating(info.getValue())}
                </div>
            ),
        }),
        columnHelper.accessor("healthInspecRating", {
            header: () => <p className="text-sm font-bold text-center text-gray-600 dark:text-white">HEALTH INSPEC</p>,
            cell: (info) => (
                <div className="flex justify-center">
                    {renderStarRating(info.getValue())}
                </div>
            ),
        }),
        columnHelper.accessor("qmRating", {
            header: () => <p className="text-sm font-bold text-center text-gray-600 dark:text-white">QM RATING</p>,
            cell: (info) => (
                <div className="flex justify-center">
                    {renderStarRating(info.getValue())}
                </div>
            ),
        }),
        columnHelper.accessor("staffingRating", {
            header: () => <p className="text-sm font-bold text-center text-gray-600 dark:text-white">STAFFING</p>,
            cell: (info) => (
                <div className="flex justify-center">
                    {renderStarRating(info.getValue())}
                </div>
            ),
        }),
        columnHelper.accessor("licensedBeds", {
            header: () => <p className="text-sm font-bold text-center text-gray-600 dark:text-white">BEDS</p>,
            cell: (info) => (
                <p className="text-center text-sm text-gray-900 font-medium">
                    {formatCompetitionValue(info.getValue(), 'Licensed Beds')}
                </p>
            ),
        }),
        columnHelper.accessor("occupancy", {
            header: () => <p className="text-sm font-bold text-center text-gray-600 dark:text-white">OCCUPANCY</p>,
            cell: (info) => (
                <p className="text-center text-sm text-gray-900 font-medium">
                    {formatCompetitionValue(info.getValue(), 'Occupancy')}
                </p>
            ),
        }),
        columnHelper.accessor("adc", {
            header: () => <p className="text-sm font-bold text-center text-gray-600 dark:text-white">ADC</p>,
            cell: (info) => (
                <p className="text-center text-sm text-gray-900 font-medium">
                    {formatCompetitionValue(info.getValue(), 'ADC')}
                </p>
            ),
        }),
        columnHelper.accessor("healthSurveyScore", {
            header: () => <p className="text-sm font-bold text-center text-gray-600 dark:text-white">HEALTH SCORE</p>,
            cell: (info) => (
                <p className="text-center text-sm text-gray-900 font-medium">
                    {formatCompetitionValue(info.getValue(), 'Health Survey Score')}
                </p>
            ),
        }),
        columnHelper.accessor("percentile", {
            header: () => <p className="text-sm font-bold text-center text-gray-600 dark:text-white">PERCENTILE</p>,
            cell: (info) => (
                <div className="flex justify-center w-full px-2">
                    {renderPercentile(info.getValue())}
                </div>
            ),
        }),

        columnHelper.accessor("owner", {
            header: () => <p className="text-sm font-bold text-center text-gray-600 dark:text-white">OWNER</p>,
            cell: (info) => (
                <p className="text-center text-sm text-gray-900 font-medium truncate max-w-[150px]">
                    {formatCompetitionValue(info.getValue(), 'Owner')}
                </p>
            ),
        }),
    ], []);

    const table = useReactTable({
        data: tableData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const handleExport = () => {
        const entityName = activeCurrentName || (isPortfolioMode ? 'portfolio' : 'deal');
        const filename = `${entityName}_${isPortfolioMode ? 'portfolio' : 'deal'}_vs_market_comparison.csv`;

        exportCompetitionTableToCSV(
            tableData,
            columns,
            filename
        );
    };

    return (
        <TableContainer
            title="Portfolio vs Market Comparison"
            onExport={handleExport}
        >
            <TableHeader headerGroups={table.getHeaderGroups()} />
            <tbody>
                {table.getRowModel().rows.map((row, index) => (
                    <tr
                        key={row.id}
                        className={`
                            border-t
                            ${row.original.type === 'Portfolio'
                                ? 'bg-blue-50 font-semibold border-t-2 border-blue-400'
                                : row.original.type === 'Competitor Average'
                                    ? 'bg-orange-50 font-semibold border-t-2 border-orange-400'
                                    : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                            }
                        `}
                    >
                        {row.getVisibleCells().map((cell, cellIndex) => {
                            const isFacilityColumn = cell.column.id === 'facility';
                            const isLastColumn = cellIndex === row.getVisibleCells().length - 1;

                            // Enhanced styling for frozen column
                            const frozenColumnStyles = isFacilityColumn ? {
                                position: 'sticky',
                                left: 0,
                                zIndex: 20,
                                backgroundColor: 'inherit',
                                boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                            } : {};

                            return (
                                <td
                                    key={cell.id}
                                    className={`px-4 py-3 text-sm border-gray-200 
                                        ${!isLastColumn ? 'border-r' : ''}
                                        ${isFacilityColumn ? 'min-w-[200px]' : 'min-w-[120px]'}`}
                                    style={frozenColumnStyles}
                                >
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </TableContainer>
    );
}

export { CompetitionSummaryTable, ConsolidatedCompetitionTable }; 